import { IGame, GameMeta, GameHostContext } from '../../platform/IGame';
import { GameRegistry } from '../../platform/GameRegistry';
import { Roulette } from './roulette';
import { ViewerRoulette } from './viewerRoulette';
import { MarbleFrame, EntityFrame, encodeFrame } from './frameCodec';
import { stages } from './data/maps';
import options from './options';

const META: GameMeta = {
  id: 'marble-roulette',
  name: 'Marble Roulette',
  description: '구슬 룰렛 게임 - 구슬들이 맵을 굴러 내려가 골인 지점에 도달하는 게임',
  minPlayers: 1,
  maxPlayers: 30,
};

class MarbleRouletteGame extends EventTarget implements IGame {
  readonly meta = META;

  private mode: 'host' | 'viewer' = 'host';
  private roulette: Roulette | null = null;
  private viewerRoulette: ViewerRoulette | null = null;
  private container: HTMLElement | null = null;

  // Host settings state
  private currentMapId = 0;
  private currentNames: string[] = [];
  private winnerType: 'first' | 'last' | 'custom' = 'first';
  private initialEntityCount = 0;

  async init(container: HTMLElement, mode: 'host' | 'viewer'): Promise<void> {
    this.mode = mode;
    this.container = container;

    if (mode === 'host') {
      this.roulette = new Roulette();
      // Wait for roulette to be ready (physics engine init)
      await new Promise<void>((resolve) => {
        const check = () => {
          if (this.roulette!.isReady) {
            resolve();
          } else {
            setTimeout(check, 100);
          }
        };
        check();
      });

      // Listen for goal event from the game engine
      this.roulette.addEventListener('goal', (e: Event) => {
        const detail = (e as CustomEvent).detail;
        this.dispatchEvent(new CustomEvent('game:finished', {
          detail: { winner: detail.winner, rank: options.winningRank },
        }));
      });
    } else {
      this.viewerRoulette = new ViewerRoulette();
      this.viewerRoulette.init(container);
    }
  }

  destroy(): void {
    if (this.viewerRoulette) {
      this.viewerRoulette.destroy();
      this.viewerRoulette = null;
    }
    this.roulette = null;
    this.container = null;
  }

  // ── HOST methods ──

  getSettingsHTML(): string {
    const mapOptions = stages.map((stage, index) =>
      `<option value="${index}">${stage.title}</option>`
    ).join('');

    return `
    <div class="right">
      <div class="row">
        <label for='sltMap'>
          <i class="icon map"></i>
          <span>맵</span>
        </label>
        <select id="sltMap">${mapOptions}</select>
      </div>
      <div class="row">
        <label>
          <i class="icon trophy"></i>
          <span>우승 조건</span>
        </label>
        <div class="btn-group">
          <button class="btn-winner btn-first-winner active">1등</button>
          <button class="btn-winner btn-last-winner">꼴등</button>
          <input type="number" id="in_winningRank" value="1" min="1" />
        </div>
      </div>
      <div class="row">
        <label for='chkSkill'>
          <i class="icon bomb"></i>
          <span>스킬 사용</span>
        </label>
        <input type="checkbox" id="chkSkill" checked />
      </div>
    </div>
    <div class="left">
      <h3>참가자 이름 입력</h3>
      <textarea id="in_names" placeholder="쉼표 또는 줄바꿈으로 이름 구분">짱구*5, 짱아*10, 봉미선*3</textarea>
      <div class="actions">
        <div class="sep"></div>
        <button id="btnShuffle">
          <i class="icon shuffle"></i>
          <span>준비</span>
        </button>
        <button id="btnStart">
          <i class="icon play"></i>
          <span>시작</span>
        </button>
      </div>
    </div>`;
  }

  bindSettings(el: HTMLElement, ctx: GameHostContext): void {
    if (!this.roulette) return;

    const textarea = el.querySelector('#in_names') as HTMLTextAreaElement;
    const mapSelector = el.querySelector('#sltMap') as HTMLSelectElement;
    const btnShuffle = el.querySelector('#btnShuffle') as HTMLButtonElement;
    const btnStart = el.querySelector('#btnStart') as HTMLButtonElement;
    const chkSkill = el.querySelector('#chkSkill') as HTMLInputElement;
    const inWinningRank = el.querySelector('#in_winningRank') as HTMLInputElement;
    const btnFirstWinner = el.querySelector('.btn-first-winner') as HTMLButtonElement;
    const btnLastWinner = el.querySelector('.btn-last-winner') as HTMLButtonElement;

    const getNames = (): string[] => {
      const value = textarea.value.trim();
      return value.split(/[,\r\n]/g).map(v => v.trim()).filter(v => !!v);
    };

    const setWinnerRank = (rank: number) => {
      inWinningRank.value = String(rank);
      options.winningRank = rank - 1;
      this.roulette!.setWinningRank(options.winningRank);

      btnFirstWinner.classList.toggle('active', this.winnerType === 'first');
      btnLastWinner.classList.toggle('active', this.winnerType === 'last');
      inWinningRank.classList.toggle('active', this.winnerType === 'custom');
    };

    const getReady = () => {
      const names = getNames();
      this.currentNames = names;
      this.roulette!.setMarbles(names);
      this.initialEntityCount = this.roulette!.entityStates.length;
      localStorage.setItem('mbr_names', names.join(','));

      switch (this.winnerType) {
        case 'first':
          setWinnerRank(1);
          break;
        case 'last': {
          const total = this.roulette!.getCount();
          setWinnerRank(total);
          break;
        }
      }

      // 준비 완료 시 시작 버튼 활성화
      btnStart.disabled = false;

      ctx.notifySetupChanged();
      this.dispatchEvent(new CustomEvent('game:setup'));
    };

    // Set initial names from host nickname (via participants)
    const participants = ctx.getParticipants();
    if (participants.length > 0) {
      textarea.value = participants[0]; // host nickname
    }

    // Expose addParticipant / removeParticipant on the game instance
    (this as any)._settingsTextarea = textarea;
    (this as any)._getReady = getReady;
    (this as any)._getNames = getNames;

    textarea.addEventListener('input', () => getReady());
    btnShuffle.addEventListener('click', () => getReady());

    btnStart.addEventListener('click', () => {
      if (this.currentNames.length === 0) return;
      this.start();
      el.classList.add('hide');
      this.dispatchEvent(new CustomEvent('game:started'));
    });

    chkSkill.addEventListener('change', (e) => {
      options.useSkills = (e.target as HTMLInputElement).checked;
    });

    inWinningRank.addEventListener('change', (e) => {
      const v = parseInt((e.target as HTMLInputElement).value, 10);
      this.winnerType = 'custom';
      setWinnerRank(isNaN(v) ? 0 : v);
    });

    btnLastWinner.addEventListener('click', () => {
      const total = this.roulette!.getCount();
      this.winnerType = 'last';
      setWinnerRank(total);
    });

    btnFirstWinner.addEventListener('click', () => {
      this.winnerType = 'first';
      setWinnerRank(1);
    });

    mapSelector.addEventListener('change', (e) => {
      this.currentMapId = parseInt((e.target as HTMLSelectElement).value);
      this.roulette!.setMap(this.currentMapId);
      getReady();
    });

    // Listen for goal to show settings again
    this.roulette.addEventListener('goal', () => {
      setTimeout(() => {
        el.classList.remove('hide');
        // 게임 종료 후 시작 버튼 비활성화 (준비를 다시 눌러야 활성화)
        btnStart.disabled = true;
        this.dispatchEvent(new CustomEvent('game:reset'));
      }, 3000);
    });

    // Initial shuffle
    btnShuffle.click();
  }

  /** Add a participant name to the textarea (called by platform when user joins) */
  addParticipant(nickname: string): void {
    const textarea = (this as any)._settingsTextarea as HTMLTextAreaElement | undefined;
    const getNames = (this as any)._getNames as (() => string[]) | undefined;
    const getReady = (this as any)._getReady as (() => void) | undefined;
    if (!textarea || !getNames || !getReady) return;

    const currentNames = getNames();
    if (currentNames.includes(nickname)) return;
    if (textarea.value.trim()) {
      textarea.value = textarea.value.trim() + ',' + nickname;
    } else {
      textarea.value = nickname;
    }
    getReady();
  }

  /** Remove a participant name from the textarea */
  removeParticipant(nickname: string): void {
    const textarea = (this as any)._settingsTextarea as HTMLTextAreaElement | undefined;
    const getNames = (this as any)._getNames as (() => string[]) | undefined;
    const getReady = (this as any)._getReady as (() => void) | undefined;
    if (!textarea || !getNames || !getReady) return;

    const currentNames = getNames();
    const filtered = currentNames.filter(n => {
      const baseName = n.replace(/[\/\*]\d+/g, '').trim();
      return baseName !== nickname;
    });
    textarea.value = filtered.join(',');
    getReady();
  }

  getSetupData(): Record<string, unknown> {
    // Send expanded names ordered by marble ID (after parse + shuffle)
    // so the viewer creates the correct number of marbles with matching names/IDs
    if (!this.roulette) return { marbleNames: [], mapId: this.currentMapId };

    const marbles = this.roulette.marbles;
    const expandedNames: string[] = new Array(marbles.length);
    for (const m of marbles) {
      expandedNames[m.id] = m.name;
    }
    return {
      marbleNames: expandedNames,
      mapId: this.currentMapId,
    };
  }

  start(): void {
    if (!this.roulette) return;
    this.roulette.start();
  }

  serializeFrame(): ArrayBuffer | null {
    if (!this.roulette) return null;

    const marbles = this.roulette.marbles;
    const winners = this.roulette.winners;
    const camera = this.roulette.camera;

    if (!marbles || !camera) return null;

    // Exclude marbles already in winners (can happen at goal event timing)
    const winnerIdSet = new Set((winners || []).map((m: any) => m.id));
    const useSkills = options.useSkills;
    const frameMarbles: MarbleFrame[] = marbles
      .filter((m: any) => !winnerIdSet.has(m.id))
      .map((m: any) => ({
        id: m.id,
        x: m.x,
        y: m.y,
        a: m.angle,
        c: useSkills ? m.coolRatio : -1,
      }));

    const winnerNames: string[] = (winners || []).map((m: any) => m.name);

    // Entity states: angle + alive for each entity (indexed by original id)
    const entityStates = this.roulette.entityStates;
    const aliveMap = new Map(entityStates.map(e => [e.id, e.angle]));
    const frameEntities: EntityFrame[] = Array.from(
      { length: this.initialEntityCount },
      (_, i) => aliveMap.has(i)
        ? { angle: aliveMap.get(i)!, alive: true }
        : { angle: 0, alive: false },
    );

    return encodeFrame(frameMarbles, winnerNames, {
      x: camera.x,
      y: camera.y,
      zoom: camera.zoom,
    }, frameEntities);
  }

  getResults(): Record<string, unknown> {
    return {};
  }

  reset(): void {
    // Reset is handled by the goal event listener in bindSettings
  }

  // ── VIEWER methods ──

  applySetup(data: Record<string, unknown>): void {
    if (!this.viewerRoulette) return;
    const names = data.marbleNames as string[];
    const mapId = (data.mapId as number) || 0;
    this.viewerRoulette.setupGame(names, mapId);
  }

  applyFrame(data: ArrayBuffer): void {
    if (!this.viewerRoulette) return;
    this.viewerRoulette.applyFrameData(data);
  }

  applyResult(data: Record<string, unknown>): void {
    if (!this.viewerRoulette) return;
    if (data.winner) {
      const rankIdx = typeof data.rank === 'number' ? data.rank : 0;
      this.viewerRoulette.showGoal(data.winner as string, rankIdx);
    }
    if (data.type === 'skill_effect') {
      this.viewerRoulette.addSkillEffect(data.x as number, data.y as number);
    }
    if (data.type === 'reset') {
      this.viewerRoulette.resetGame();
    }
    if (data.type === 'start') {
      this.viewerRoulette.startGame();
    }
  }
}

// ── Register with the platform ──
GameRegistry.register(META, () => new MarbleRouletteGame());
